import React, { useEffect, useState } from "react";
import {
  Form,
  FormGroup,
  Input,
  Label,
  Button,
  Row,
  Col,
  Container,
  CardHeader,
} from "reactstrap";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaFile,
  FaFileAlt,
  FaImage,
  FaHandshake,
  FaBuilding,
} from "react-icons/fa";
import { HiOutlineHashtag } from "react-icons/hi";

import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  getResearchPaperByIdApi,
  submitResearchPaperApi,
} from "src/network/api";
import { toast } from "react-toastify";
import CountrySelector from "./components/CountrySelector";

export interface ResearchPaperData {
  paper_title: string;
  abstract: string;
  keywords: string;
  subjects: string;
  file: File | null;
  author_name: string;
  author_email: string;
  author_contact: string;
  country: string;
  department: string;
  affiliation: string;
  co_authors: Array<{
    co_author_name: string;
    co_author_email: string;
    co_author_contact: string;
    co_author_country: string;
    co_author_department: string;
    co_author_affiliation: string;
  }>;
}

export const ResearchPaperForm = ({
  researchPaperId = null,
  onSubmit,
  submitButton,
}: {
  researchPaperId?: string;
  onSubmit: (data: ResearchPaperData) => void;
  submitButton: JSX.Element;
}) => {
  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ResearchPaperData>({
    mode: "onSubmit",
    defaultValues: {
      paper_title: "",
      abstract: "",
      keywords: "",
      subjects: "",
      file: null,
      author_name: "",
      author_email: "",
      author_contact: "",
      country: "",
      department: "",
      affiliation: "",
      co_authors: [],
    },
  });

  //const [formGroups, setFormGroups] = useState<ResearchPaperData['co_authors']>([]);
  const [loading, setIsLoading] = useState(false);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "co_authors",
  });

  const handleAdd = () => {
    append({
      co_author_name: "",
      co_author_email: "",
      co_author_contact: "",
      co_author_country: "",
      co_author_department: "",
      co_author_affiliation: "",
    });
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    console.log("research paper ID:", researchPaperId);
    if (researchPaperId) {
      setIsLoading(true);
      const fetchJournal = async () => {
        try {
          const response = await getResearchPaperByIdApi(researchPaperId);
          if (response.status === 200 && response.data.status === 200) {
            const details: ResearchPaperData = response.data.data;
            let fValues = getValues();

            for (let key in fValues) {
              if (key in details) {
                setValue(key as keyof ResearchPaperData, details[key]);
              }
            }

            let coAuthors = [];
            if (typeof details.co_authors === "string") {
              try {
                coAuthors = JSON.parse(details.co_authors);
              } catch (error) {
                console.error("Error parsing co_authors:", error);
                coAuthors = [];
              }
            } else if (Array.isArray(details.co_authors)) {
              coAuthors = details.co_authors;
            }

            replace(
              coAuthors.map((author) => ({
                co_author_name: author.co_author_name || "",
                co_author_email: author.co_author_email || "",
                co_author_contact: author.co_author_contact || "",
                co_author_country: author.co_author_country || "",
                co_author_department: author.co_author_department || "",
                co_author_affiliation: author.co_author_affiliation || "",
              }))
            );
          } else {
            toast.error(
              response.data.message ||
                "No data found for the given research paper ID."
            );
          }
        } catch (e: any) {
          toast.error(e.response?.data?.message ?? e.message);
        } finally {
          setIsLoading(false);
        }
      };
      fetchJournal();
    }
  }, [researchPaperId, replace]);

  if (loading) {
    return <p className="text-center">Please wait...</p>;
  }
  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {/* Paper Title */}
      <FormGroup>
        <Label for="paper_title" className="font-weight-bold">
          <FaFile /> Paper Title
          <span className="text-danger">*</span>
        </Label>
        <Controller
          control={control}
          name="paper_title"
          rules={{ required: "Paper title is required" }}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              id="paper_title"
              placeholder="Enter research paper title"
              className="form-control"
            />
          )}
        />
        {errors.paper_title && (
          <div className="error-text">
            {errors.paper_title.message as string}
          </div>
        )}
      </FormGroup>

      <FormGroup>
        <Label for="abstract" className="font-weight-bold">
          <FaFileAlt /> Abstract
          {/* <span className="text-danger">*</span> */}
        </Label>
        <Controller
          control={control}
          name="abstract"
          // rules={{ required: "Abstract is required" }}
          render={({ field }) => (
            <Input
              {...field}
              type="textarea"
              id="abstract"
              placeholder="Enter research abstract"
              className="form-control"
            />
          )}
        />
        {errors.abstract && (
          <div className="error-text">{errors.abstract.message as string}</div>
        )}
      </FormGroup>

      <FormGroup>
        <Label for="keywords" className="font-weight-bold">
          <HiOutlineHashtag className="mr-0" /> Keywords
          {/* <span className="text-danger">*</span> */}
        </Label>
        <Controller
          control={control}
          name="keywords"
          // rules={{ required: "Keywords are required" }}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              id="keywords"
              placeholder="Enter keywords (comma-separated)"
              className="form-control"
            />
          )}
        />
        {errors.keywords && (
          <div className="error-text">{errors.keywords.message as string}</div>
        )}
      </FormGroup>
      <FormGroup>
        <Label for="subjects" className="font-weight-bold">
          <HiOutlineHashtag className="mr-0" />
          Subject
          <span className="text-danger">*</span>
        </Label>
        <Controller
          control={control}
          name="subjects"
          rules={{ required: "Subjects are required" }}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              id="subjects"
              placeholder="Enter subjects (comma-separated)"
              className="form-control"
            />
          )}
        />
        {errors.subjects && (
          <div className="error-text">{errors.subjects.message as string}</div>
        )}
      </FormGroup>

      {/* File Upload */}
      <FormGroup>
        <Label for="file" className="font-weight-bold">
          <FaImage className="mr-2" /> Upload File (doc/*)
          <span className="text-danger">*</span>
        </Label>
        <Controller
          control={control}
          name="file"
          rules={{ required: "File is required" }}
          render={({ field }) => (
            <Input
              type="file"
              id="file"
              className="form-control-file"
              onChange={(event) =>
                field.onChange(event.target.files?.[0] || null)
              }
            />
          )}
        />
        {errors.file && (
          <div className="error-text">{errors.file.message as string}</div>
        )}
      </FormGroup>

      <div className="mb-5 border p-3">
        <FormGroup>
          <Label for="author_name" className="font-weight-bold">
            <FaUser className="mr-2" /> Author name
            <span className="text-danger">*</span>
          </Label>
          <Controller
            control={control}
            name="author_name"
            rules={{ required: "Name are required" }}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                id="keywords"
                placeholder="Enter author_name "
                className="form-control"
              />
            )}
          />
          {errors.author_name && (
            <div className="error-text">
              {errors.author_name.message as string}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="author_email" className="font-weight-bold">
            <FaEnvelope className="mr-2" /> Author Email
            <span className="text-danger">*</span>
          </Label>
          <Controller
            control={control}
            name="author_email"
            rules={{ required: "Email are required" }}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                id="author_email"
                placeholder="Enter author_email "
                className="form-control"
              />
            )}
          />
          {errors.author_email && (
            <div className="error-text">
              {errors.author_email.message as string}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="author_contact" className="font-weight-bold">
            <FaPhone className="mr-2" /> Contact
            <span className="text-danger">*</span>
          </Label>
          <Controller
            control={control}
            name="author_contact"
            rules={{ required: "Contact are required" }}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                id="author_contact"
                placeholder="Enter author_contact "
                className="form-control"
              />
            )}
          />
          {errors.author_contact && (
            <div className="error-text">
              {errors.author_contact.message as string}
            </div>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="country" className="font-weight-bold">
            <FaGlobe className="mr-2" /> Country
            <span className="text-danger">*</span>
          </Label>
          <Controller
            name="country"
            control={control}
            rules={{ required: "Country is required" }}
            render={({ field }) => (
              <CountrySelector
                currentCountry={field.value}
                onSelectCountryCallback={(selectedCountry) => {
                  field.onChange(selectedCountry);
                }}
              />
            )}
          />
          {errors.country && (
            <div className="error-text">{errors.country.message as string}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="department" className="font-weight-bold">
            <FaBuilding /> Department/College
          </Label>
          <Controller
            control={control}
            name="department"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                id="department"
                placeholder="Enter department"
              />
            )}
          />
        </FormGroup>

        <FormGroup>
          <Label for="affiliation" className="font-weight-bold">
            <FaFileAlt className="mr-2" /> University/Affiliation
          </Label>
          <Controller
            control={control}
            name="affiliation"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                id="affiliation"
                placeholder="Enter affiliation"
              />
            )}
          />
        </FormGroup>
      </div>

      {fields.map((item, index) => (
        <div key={item.id} className="mb-5 border p-3">
          <div className="d-flex justify-content-between">
            <h5>Co-author</h5>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleRemove(index)}
            >
              &#10005;
            </Button>
          </div>
          {/* Co-author Name */}
          <FormGroup>
            <Label for={`co_author_name_${index}`} className="font-weight-bold">
              Author Name
            </Label>
            <Controller
              control={control}
              name={`co_authors.${index}.co_author_name`}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id={`co_author_name_${index}`}
                  placeholder="Enter author name"
                />
              )}
            />
          </FormGroup>

          {/* Co-author Email */}
          <FormGroup>
            <Label
              for={`co_author_email_${index}`}
              className="font-weight-bold"
            >
              Author Email
            </Label>
            <Controller
              control={control}
              name={`co_authors.${index}.co_author_email`}
              render={({ field }) => (
                <Input
                  {...field}
                  type="email"
                  id={`co_author_email_${index}`}
                  placeholder="Enter author email"
                />
              )}
            />
          </FormGroup>

          {/* Co-author Contact */}
          <FormGroup>
            <Label
              for={`co_author_contact_${index}`}
              className="font-weight-bold"
            >
              Contact
            </Label>
            <Controller
              control={control}
              name={`co_authors.${index}.co_author_contact`}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id={`co_author_contact_${index}`}
                  placeholder="Enter contact"
                />
              )}
            />
          </FormGroup>

          {/* Co-author Country */}
          <FormGroup>
            <Label
              for={`co_author_country_${index}`}
              className="font-weight-bold"
            >
              Co-Author Country
            </Label>
            <Controller
              control={control}
              name={`co_authors.${index}.co_author_country`}
              render={({ field }) => (
                <CountrySelector
                  currentCountry={field.value}
                  onSelectCountryCallback={(selectedCountry) =>
                    field.onChange(selectedCountry)
                  }
                />
              )}
            />
          </FormGroup>

          {/* Co-author Department */}
          <FormGroup>
            <Label
              for={`co_author_department_${index}`}
              className="font-weight-bold"
            >
              Department/College
            </Label>
            <Controller
              control={control}
              name={`co_authors.${index}.co_author_department`}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id={`co_author_department_${index}`}
                  placeholder="Enter department"
                />
              )}
            />
          </FormGroup>

          {/* Co-author Affiliation */}
          <FormGroup>
            <Label
              for={`co_author_affiliation_${index}`}
              className="font-weight-bold"
            >
              University/Affiliation
            </Label>
            <Controller
              control={control}
              name={`co_authors.${index}.co_author_affiliation`}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id={`co_author_affiliation_${index}`}
                  placeholder="Enter affiliation"
                />
              )}
            />
          </FormGroup>
        </div>
      ))}

      <div className="mt-3">
        <Button color="success" className="mb-3" onClick={handleAdd}>
          Add Co-authors
        </Button>
      </div>

      {/* Submit Button */}
      <FormGroup>
        <Button type="submit" color="primary" className="btn-block">
          SUBMIT RESEARCH PAPER
        </Button>
      </FormGroup>
    </Form>
  );
};
